import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import AppHeader from "@/components/AppHeader";
import KpiCard from "@/components/KpiCard";
import TriageDrawer from "@/components/TriageDrawer";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import VoiceNotesPanel from "@/components/VoiceNotesPanel";
import SpeakButton from "@/components/SpeakButton";
import PaymentLedgerPanel from "@/components/PaymentLedgerPanel";
import WhatsAppLogsPanel from "@/components/WhatsAppLogsPanel";
import DrugAutoSuggest from "@/components/DrugAutoSuggest";
import RoadmapDialog from "@/components/RoadmapDialog";
import OnboardingMeter from "@/components/OnboardingMeter";
import Tip from "@/components/Tip";
import { Banknote, Clock4, Users, CalendarClock, Stethoscope, Mic, Square, Send, FileDown, RefreshCw, AlertTriangle, MessageCircle, MessageSquareShare, FileText, Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const fmtINR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const fmtTime = (iso) => new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

export default function ClinicDashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const clinicId = user.clinic_id;
  const [dash, setDash] = useState(null);
  const [tab, setTab] = useState("overview");
  const [voicePrefill, setVoicePrefill] = useState(null);

  const refresh = () => api.get(`/clinics/${clinicId}/dashboard`).then((r) => setDash(r.data));
  useEffect(() => { refresh(); }, []); // eslint-disable-line

  // Pick up cross-page tab jumps (set from OnboardingMeter / RoadmapDialog)
  useEffect(() => {
    const jump = sessionStorage.getItem("pulse_jump_tab");
    if (jump) {
      setTab(jump);
      sessionStorage.removeItem("pulse_jump_tab");
    }
  }, []);

  if (!dash) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;

  const c = dash.clinic;

  return (
    <div className="min-h-screen app-bg pb-16">
      <RoadmapDialog />
      <AppHeader title={c?.name || "Clinic"} subtitle={c?.address} logoUrl={c?.logo_url} badge="Clinic-Admin" />

      <AnnouncementBanner clinicId={clinicId} />

      <main className="max-w-[1500px] mx-auto px-4 sm:px-6 mt-6 sm:mt-8">
        <div className="flex items-end justify-between mb-5 sm:mb-6 flex-wrap gap-3">
          <div className="min-w-0">
            <div className="tiny-label text-[#f37021]">White-labeled Dashboard</div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold tracking-tight" data-testid="clinic-dashboard-heading">
              {t("dash_hi")} {user.name.split(" ")[1] ? `Dr. ${user.name.split(" ").slice(-1)}` : user.name},
              <span className="text-[#004a99]"> {t("dash_pulse")}</span>
            </h1>
          </div>
          <Button variant="outline" onClick={refresh} className="border-[#004a99] text-[#004a99]" data-testid="refresh-dashboard-btn">
            <RefreshCw className="h-4 w-4 mr-1.5 sm:mr-2" /> <span className="hidden sm:inline">{t("dash_refresh")}</span><span className="sm:hidden">Refresh</span>
          </Button>
        </div>

        {/* Onboarding meter */}
        <div className="mb-5 sm:mb-6">
          <OnboardingMeter clinicId={clinicId} userId={user.id} />
        </div>

        {/* KPI Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5 mb-6 sm:mb-8">
          <KpiCard testId="kpi-revenue-recovered" label={t("kpi_revenue")} value={fmtINR(dash.revenue_recovered)} sublabel={t("kpi_invoices", { n: dash.invoices_recovered })} icon={Banknote} accent="orange" />
          <KpiCard testId="kpi-time-saved" label={t("kpi_time")} value={`${dash.hours_saved} hrs`} sublabel={t("kpi_triage_scribe")} icon={Clock4} accent="blue" />
          <KpiCard testId="kpi-overdue" label={t("kpi_overdue")} value={fmtINR(dash.overdue_amount)} sublabel={t("kpi_pending", { n: dash.overdue_count })} icon={AlertTriangle} accent="slate" />
          <KpiCard testId="kpi-today" label={t("kpi_today")} value={dash.appointments_today} sublabel={`${dash.triage_sessions} triage · ${dash.clinical_notes} notes`} icon={CalendarClock} accent="green" />
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-white border border-slate-200 p-1 rounded-xl flex flex-wrap h-auto gap-1 justify-start overflow-x-auto">
            <TabsTrigger value="overview" data-testid="tab-overview">{t("tab_overview")}</TabsTrigger>
            <TabsTrigger value="ledger" data-testid="tab-ledger">{t("tab_ledger")}</TabsTrigger>
            <TabsTrigger value="appointments" data-testid="tab-appointments">{t("tab_appointments")}</TabsTrigger>
            <TabsTrigger value="voice" data-testid="tab-voice">{t("tab_voice")}</TabsTrigger>
            <TabsTrigger value="voicenotes" data-testid="tab-voicenotes">{t("tab_voicenotes")}</TabsTrigger>
            <TabsTrigger value="triage" data-testid="tab-triage">{t("tab_triage")}</TabsTrigger>
            <TabsTrigger value="whatsapp" data-testid="tab-whatsapp">{t("tab_whatsapp")}</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            <OverviewPanel clinicId={clinicId} />
          </TabsContent>
          <TabsContent value="ledger" className="mt-6">
            <PaymentLedgerPanel clinicId={clinicId} onChanged={refresh} />
          </TabsContent>
          <TabsContent value="appointments" className="mt-6">
            <AppointmentsPanel clinicId={clinicId} onChanged={refresh} />
          </TabsContent>
          <TabsContent value="voice" className="mt-6">
            <VoiceEmrPanel clinicId={clinicId} onChanged={refresh} prefill={voicePrefill} clearPrefill={() => setVoicePrefill(null)} />
          </TabsContent>
          <TabsContent value="voicenotes" className="mt-6">
            <VoiceNotesPanel clinicId={clinicId} />
          </TabsContent>
          <TabsContent value="triage" className="mt-6">
            <TriagePanel clinicId={clinicId} onConvert={(payload) => { setVoicePrefill(payload); setTab("voice"); toast.success("Pre-filled Voice-to-EMR. Edit & generate the note."); }} />
          </TabsContent>
          <TabsContent value="whatsapp" className="mt-6">
            <WhatsAppLogsPanel clinicId={clinicId} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function OverviewPanel({ clinicId }) {
  const [patients, setPatients] = useState([]);
  const [shareOpen, setShareOpen] = useState(null);
  const [sharePhone, setSharePhone] = useState("");
  useEffect(() => { api.get(`/clinics/${clinicId}/patients`).then(r => setPatients(r.data)); }, [clinicId]);

  const downloadHistory = (pid) => {
    const token = localStorage.getItem("pulse_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clinics/${clinicId}/patients/${pid}/history.pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => r.blob()).then(b => window.open(URL.createObjectURL(b), "_blank"));
  };

  const shareHistory = async (p) => {
    const phone = sharePhone || p.phone;
    if (!phone) return toast.error("Phone required");
    try {
      const r = await api.post(`/clinics/${clinicId}/patients/${p.id}/share-history-whatsapp`, { patient_phone: phone });
      toast.success(`History link sent to ${phone}${r.data.whatsapp?.simulated ? " (simulated)" : ""}`);
      navigator.clipboard?.writeText(r.data.public_url);
      setTimeout(() => toast.message("Link copied to clipboard", { description: r.data.public_url }), 300);
      setShareOpen(null); setSharePhone("");
    } catch { toast.error("Share failed"); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <div className="solid-panel p-0 lg:col-span-2 overflow-hidden shadow-3d">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-lg font-heading font-semibold flex items-center gap-2"><Users className="h-4 w-4 text-[#004a99]" /> Patients</h3>
          <span className="tiny-label">{patients.length} total</span>
        </div>
        <Table>
          <TableHeader>
            <TableRow><TableHead>Name</TableHead><TableHead>Phone</TableHead><TableHead>Age</TableHead><TableHead>Last visit</TableHead><TableHead className="text-right">History</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {patients.map(p => (
              <TableRow key={p.id} data-testid={`patient-row-${p.id}`}>
                <TableCell className="font-semibold">{p.name}</TableCell>
                <TableCell>{p.phone}</TableCell>
                <TableCell>{p.age}</TableCell>
                <TableCell>{p.last_visit ? new Date(p.last_visit).toLocaleDateString() : "—"}</TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => downloadHistory(p.id)} className="border-[#004a99] text-[#004a99]" data-testid={`download-history-${p.id}`}>
                      <FileDown className="h-3.5 w-3.5 mr-1" /> PDF
                    </Button>
                    <Button size="sm" onClick={() => { setShareOpen(p); setSharePhone(p.phone || ""); }} className="bg-[#25D366] hover:bg-[#1ea655] text-white" data-testid={`share-history-${p.id}`}>
                      <MessageSquareShare className="h-3.5 w-3.5 mr-1" /> WhatsApp
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="tilt-card glass-panel rounded-2xl p-6 shadow-3d">
        <div className="tiny-label">Why doctors love it</div>
        <ul className="mt-3 space-y-3 text-slate-700 text-sm">
          <li className="flex gap-2"><Stethoscope className="h-4 w-4 text-[#004a99] mt-0.5" /> AI sorts urgent vs routine before patient arrives.</li>
          <li className="flex gap-2"><Banknote className="h-4 w-4 text-[#f37021] mt-0.5" /> Overdue invoices get nudged automatically.</li>
          <li className="flex gap-2"><Mic className="h-4 w-4 text-emerald-700 mt-0.5" /> Voice notes become structured PDFs in seconds.</li>
          <li className="flex gap-2"><FileText className="h-4 w-4 text-[#004a99] mt-0.5" /> Complete patient history exportable as PDF anytime.</li>
        </ul>
      </div>

      <Dialog open={!!shareOpen} onOpenChange={(o) => !o && setShareOpen(null)}>
        <DialogContent data-testid="share-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Send patient history on WhatsApp</DialogTitle>
            <p className="text-sm text-slate-500">Generates a secure download link for the full PDF dossier.</p>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Patient</Label>
              <Input value={shareOpen?.name || ""} readOnly className="bg-slate-50" />
            </div>
            <div>
              <Label>WhatsApp number *</Label>
              <Input value={sharePhone} onChange={(e) => setSharePhone(e.target.value)} data-testid="share-history-phone" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShareOpen(null)}>Cancel</Button>
            <Button onClick={() => shareHistory(shareOpen)} className="bg-[#25D366] hover:bg-[#1ea655] text-white" data-testid="share-history-confirm">
              <Send className="h-4 w-4 mr-1" /> Send link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RecoveryPanel({ clinicId, onChanged }) {
  const [invoices, setInvoices] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/clinics/${clinicId}/invoices`).then(r => setInvoices(r.data));
  useEffect(() => { load(); }, [clinicId]); // eslint-disable-line

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/clinics/${clinicId}/recovery/run`);
      const sim = r.data.sample_messages?.some(m => m.simulated);
      toast.success(`${r.data.sent} reminder(s) sent · ${r.data.scanned} scanned${sim ? " (simulated)" : ""}`);
      load(); onChanged?.();
    } catch { toast.error("Recovery run failed"); } finally { setBusy(false); }
  };

  const markPaid = async (id) => {
    await api.post(`/clinics/${clinicId}/invoices/${id}/mark-paid`);
    toast.success("Invoice marked paid");
    load(); onChanged?.();
  };

  return (
    <div className="solid-panel p-0 overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-heading font-semibold">Revenue Recovery Agent</h3>
          <p className="text-sm text-slate-500">Background worker scans overdue invoices and pushes WhatsApp nudges.</p>
        </div>
        <Button onClick={run} disabled={busy} className="bg-[#f37021] hover:bg-[#d9621b] text-white" data-testid="run-recovery-btn">
          <Send className="h-4 w-4 mr-2" /> {busy ? "Running…" : "Run Recovery Now"}
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Patient</TableHead><TableHead>Phone</TableHead><TableHead>Amount</TableHead>
            <TableHead>Due</TableHead><TableHead>Status</TableHead><TableHead>Reminders</TableHead><TableHead className="text-right">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {invoices.map(inv => (
            <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
              <TableCell className="font-semibold">{inv.patient_name}</TableCell>
              <TableCell>{inv.patient_phone}</TableCell>
              <TableCell>₹{inv.amount.toFixed(0)}</TableCell>
              <TableCell>{new Date(inv.due_at).toLocaleDateString()}</TableCell>
              <TableCell>
                <span className={`text-xs font-semibold rounded-full px-2.5 py-1 ${
                  inv.status === "paid" ? "bg-emerald-50 text-emerald-700"
                  : inv.status === "overdue" ? "bg-rose-50 text-rose-700"
                  : "bg-slate-100 text-slate-600"}`}>
                  {inv.status}
                </span>
              </TableCell>
              <TableCell>{inv.reminder_count}</TableCell>
              <TableCell className="text-right">
                {inv.status !== "paid" && (
                  <Button size="sm" variant="outline" onClick={() => markPaid(inv.id)} data-testid={`mark-paid-${inv.id}`}>Mark Paid</Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function AppointmentsPanel({ clinicId, onChanged }) {
  const [appts, setAppts] = useState([]);
  const [date, setDate] = useState(new Date());
  const load = () => api.get(`/clinics/${clinicId}/appointments`).then(r => setAppts(r.data));
  useEffect(() => { load(); }, [clinicId]); // eslint-disable-line

  const dayKey = (d) => new Date(d).toDateString();
  const filtered = useMemo(() => appts.filter(a => dayKey(a.scheduled_at) === dayKey(date)), [appts, date]);
  const booked = useMemo(() => new Set(appts.map(a => dayKey(a.scheduled_at))), [appts]);

  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <div className="solid-panel p-4 lg:col-span-1">
        <h3 className="text-lg font-heading font-semibold mb-2">Calendar</h3>
        <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)} className="rounded-md"
                  modifiers={{ booked: (d) => booked.has(d.toDateString()) }}
                  modifiersStyles={{ booked: { backgroundColor: "#ffefe6", color: "#f37021", fontWeight: 700 } }} />
        <div className="mt-3 text-xs text-slate-500 flex items-center gap-2">
          <span className="inline-block h-3 w-3 rounded-full bg-[#ffefe6] border border-[#f37021]" /> Days with bookings
        </div>
      </div>

      <div className="solid-panel lg:col-span-2 p-0 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-lg font-heading font-semibold">{date.toDateString()}</h3>
          <span className="tiny-label">{filtered.length} appts</span>
        </div>
        {filtered.length === 0 && <div className="p-10 text-center text-slate-400 text-sm">No appointments on this day. AI triage bookings will land here automatically.</div>}
        <div className="divide-y divide-slate-100">
          {filtered.map(a => (
            <div key={a.id} className="px-6 py-4 flex items-center gap-4" data-testid={`appt-row-${a.id}`}>
              <div className="h-10 w-10 rounded-xl bg-[#e6f0ff] flex items-center justify-center text-[#004a99] font-bold">
                {new Date(a.scheduled_at).getHours().toString().padStart(2, "0")}
              </div>
              <div className="flex-1">
                <div className="font-semibold text-slate-900">{a.patient_name}</div>
                <div className="text-xs text-slate-500">{fmtTime(a.scheduled_at)} · {a.duration_min}m · {a.reason || "Consultation"}</div>
              </div>
              <span className={`text-xs font-semibold rounded-full px-2.5 py-1 ${a.source === "ai_triage" ? "bg-[#ffefe6] text-[#f37021]" : "bg-[#e6f0ff] text-[#004a99]"}`}>
                {a.source === "ai_triage" ? "AI Triage" : "Manual"}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function VoiceEmrPanel({ clinicId, onChanged, prefill, clearPrefill }) {
  const [transcript, setTranscript] = useState("Patient is a 32-year-old female presenting with cough and mild fever for 3 days. Mild sore throat. No shortness of breath. Examination: throat congested, lungs clear. Plan: rest, paracetamol 500mg three times a day for 3 days, salt water gargle, follow up if symptoms worsen.");
  const [patientName, setPatientName] = useState("Priya Verma");
  const [patientPhone, setPatientPhone] = useState("+919811000010");
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState([]);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const tickRef = useRef(null);
  const [waNote, setWaNote] = useState(null);
  const [waPhone, setWaPhone] = useState("");

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("audio", blob, "voice-emr.webm");
          const token = localStorage.getItem("pulse_token");
          const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clinics/${clinicId}/voice-notes/transcribe-only`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: fd,
          });
          const data = await r.json();
          if (data.text) {
            setTranscript((t) => (t ? `${t}\n\n${data.text}` : data.text));
            toast.success(`Transcribed (${data.language})`);
          } else {
            toast.error(data.error || "Empty transcript");
          }
        } catch { toast.error("Transcribe failed"); } finally { setTranscribing(false); }
      };
      mr.start();
      setRecording(true);
      setSeconds(0);
      tickRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch { toast.error("Microphone access denied"); }
  };

  const stopRec = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    setRecording(false);
  };

  const load = () => api.get(`/clinics/${clinicId}/clinical-notes`).then(r => setNotes(r.data));
  useEffect(() => { load(); }, [clinicId]); // eslint-disable-line

  useEffect(() => {
    if (prefill) {
      setTranscript(prefill.transcript);
      setPatientName(prefill.patient_name || "Patient");
      if (prefill.patient_phone) setPatientPhone(prefill.patient_phone);
      clearPrefill?.();
    }
  }, [prefill]); // eslint-disable-line

  const generate = async () => {
    if (!transcript.trim()) return toast.error("Transcript is empty");
    setBusy(true);
    try {
      await api.post(`/clinics/${clinicId}/clinical-notes`, { transcript, patient_name: patientName, patient_phone: patientPhone });
      toast.success("Clinical note generated");
      load(); onChanged?.();
    } catch { toast.error("Generation failed"); } finally { setBusy(false); }
  };

  const downloadPdf = (id) => {
    const token = localStorage.getItem("pulse_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clinics/${clinicId}/clinical-notes/${id}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => r.blob()).then(b => {
      const url = URL.createObjectURL(b);
      window.open(url, "_blank");
    });
  };

  const sendWa = async (note) => {
    const phone = waPhone || note.patient_phone || patientPhone;
    if (!phone) return toast.error("Patient phone required");
    try {
      const r = await api.post(`/clinics/${clinicId}/clinical-notes/${note.id}/send-whatsapp`, { patient_phone: phone });
      toast.success(`PDF link sent to ${phone}${r.data.whatsapp?.simulated ? " (simulated — link below)" : ""}`);
      setWaNote(null); setWaPhone("");
      navigator.clipboard?.writeText(r.data.public_url);
      // Also surface the link in a follow-up toast for demo
      setTimeout(() => toast.message("Public link copied to clipboard", { description: r.data.public_url }), 300);
    } catch { toast.error("Send failed"); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <div className="solid-panel p-6 shadow-3d">
        <h3 className="text-lg font-heading font-semibold flex items-center gap-2"><Mic className="h-4 w-4 text-[#f37021]" /> Voice-to-EMR Scribe</h3>
        <p className="text-sm text-slate-500 mt-1">Tap mic → speak → Whisper transcribes → Claude structures it into a PDF prescription.</p>

        <div className="mt-4 flex items-center justify-center rounded-2xl bg-gradient-to-br from-[#e6f0ff] to-white border border-slate-200 p-6">
          <button
            onClick={recording ? stopRec : startRec}
            disabled={transcribing}
            className={`h-20 w-20 rounded-full flex items-center justify-center transition-all shadow-lg ${recording ? "bg-[#ef4444] scale-110 animate-pulse" : "bg-[#004a99] hover:bg-[#003b7a]"} text-white disabled:opacity-50`}
            data-testid="voice-record-btn"
          >
            {recording ? <Square className="h-8 w-8" /> : <Mic className="h-8 w-8" />}
          </button>
          <div className="ml-5 flex-1">
            <div className="tiny-label">{transcribing ? "Transcribing…" : recording ? "Recording" : "Tap to record"}</div>
            <div className="text-3xl font-heading font-bold text-slate-900 mt-1">
              {Math.floor(seconds / 60).toString().padStart(2, "0")}:{(seconds % 60).toString().padStart(2, "0")}
            </div>
            <div className="text-xs text-slate-500">Tap again to stop. Auto-fills transcript.</div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          <Input value={patientName} onChange={(e) => setPatientName(e.target.value)} placeholder="Patient name" data-testid="voice-patient-input" />
          <Input value={patientPhone} onChange={(e) => setPatientPhone(e.target.value)} placeholder="Patient WhatsApp number" data-testid="voice-phone-input" />
        </div>
        <Textarea className="mt-3 min-h-[180px]" value={transcript} onChange={(e) => setTranscript(e.target.value)} data-testid="voice-transcript-input" />

        {/* Smart Prescription drug suggestion */}
        <div className="mt-3">
          <div className="text-xs font-semibold text-slate-700 mb-1 flex items-center">
            Smart drug autosuggest
            <Tip text="Type 2+ letters to search a curated list of 50 common Indian drugs. Picking one appends 'Rx: <drug> <dose> <freq> <duration>' to your transcript." />
          </div>
          <DrugAutoSuggest onPick={(d) => {
            const line = `\nRx: ${d.name} ${d.default_dose} · ${d.default_freq} · ${d.default_duration}`;
            setTranscript((prev) => (prev ? prev + line : line.trimStart()));
            toast.success(`Added ${d.name} to transcript`);
          }} />
        </div>

        <Button onClick={generate} disabled={busy} className="mt-3 w-full bg-[#f37021] hover:bg-[#d9621b] text-white" data-testid="voice-generate-btn">
          {busy ? "Generating…" : "Generate Structured Note"}
          <Tip text="If your doctor signature is missing, you'll be redirected to Profile. Save signature there, then come back." />
        </Button>
      </div>

      <div className="solid-panel p-0 overflow-hidden shadow-3d">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-lg font-heading font-semibold">Recent Notes</h3>
          <span className="tiny-label">{notes.length}</span>
        </div>
        {notes.length === 0 && <div className="p-10 text-center text-slate-400 text-sm">No notes yet. Generate your first one →</div>}
        <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
          {notes.map(n => (
            <div key={n.id} className="p-5" data-testid={`note-${n.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="font-semibold text-slate-900">{n.patient_name}</div>
                  <div className="text-xs text-slate-500">{new Date(n.created_at).toLocaleString()}{n.whatsapp_sent_at && " · sent on WhatsApp"}</div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => downloadPdf(n.id)} className="border-[#004a99] text-[#004a99]" data-testid={`note-pdf-${n.id}`}>
                    <FileDown className="h-4 w-4 mr-1" /> PDF
                  </Button>
                  <Button size="sm" onClick={() => { setWaNote(n); setWaPhone(n.patient_phone || ""); }} className="bg-[#25D366] hover:bg-[#1ea655] text-white" data-testid={`note-wa-${n.id}`}>
                    <MessageSquareShare className="h-4 w-4 mr-1" /> WhatsApp
                  </Button>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="tiny-label flex items-center justify-between">
                    <span>Chief Complaint</span>
                    {n.structured?.chief_complaint && <SpeakButton text={n.structured.chief_complaint} testId={`speak-cc-${n.id}`} />}
                  </div>
                  <div className="text-slate-700 mt-1">{n.structured?.chief_complaint}</div>
                </div>
                <div>
                  <div className="tiny-label flex items-center justify-between">
                    <span>Assessment</span>
                    {n.structured?.assessment && <SpeakButton text={n.structured.assessment} testId={`speak-as-${n.id}`} />}
                  </div>
                  <div className="text-slate-700 mt-1">{n.structured?.assessment}</div>
                </div>
              </div>
              {n.structured?.prescription?.length > 0 && (
                <div className="mt-3">
                  <div className="tiny-label mb-1">Prescription</div>
                  <ul className="text-sm text-slate-700 list-disc pl-5">
                    {n.structured.prescription.map((p, i) => (
                      <li key={i}><b>{p.drug}</b> {p.dose} · {p.frequency} · {p.duration}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!waNote} onOpenChange={(o) => !o && setWaNote(null)}>
        <DialogContent data-testid="wa-send-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Send prescription on WhatsApp</DialogTitle>
            <p className="text-sm text-slate-500">Patient gets a secure link to download the PDF.</p>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Patient name</Label>
              <Input value={waNote?.patient_name || ""} readOnly className="bg-slate-50" />
            </div>
            <div>
              <Label>WhatsApp number *</Label>
              <Input value={waPhone} onChange={(e) => setWaPhone(e.target.value)} placeholder="+9198..." data-testid="wa-phone-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWaNote(null)}>Cancel</Button>
            <Button onClick={() => sendWa(waNote)} className="bg-[#25D366] hover:bg-[#1ea655] text-white" data-testid="wa-send-confirm">
              <Send className="h-4 w-4 mr-1" /> Send PDF link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TriagePanel({ clinicId, onConvert }) {
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => { api.get(`/clinics/${clinicId}/triage-sessions`).then(r => setSessions(r.data)); }, [clinicId]);

  const openRow = (id) => { setActiveId(id); setOpen(true); };

  return (
    <>
    <div className="solid-panel p-0 overflow-hidden shadow-3d">
      <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
        <h3 className="text-lg font-heading font-semibold flex items-center gap-2"><MessageCircle className="h-4 w-4 text-[#004a99]" /> WhatsApp Triage Sessions</h3>
        <span className="tiny-label">{sessions.length} · click any row to read full chat</span>
      </div>
      {sessions.length === 0 && <div className="p-10 text-center text-slate-400 text-sm">No triage sessions yet. Patients can start one from the chat interface.</div>}
      <Table>
        <TableHeader><TableRow><TableHead>Patient</TableHead><TableHead>Phone</TableHead><TableHead>Level</TableHead><TableHead>Summary</TableHead><TableHead>Status</TableHead><TableHead>Started</TableHead></TableRow></TableHeader>
        <TableBody>
          {sessions.map(s => (
            <TableRow key={s.id} className="cursor-pointer hover:bg-slate-50" onClick={() => openRow(s.id)} data-testid={`triage-row-${s.id}`}>
              <TableCell className="font-semibold">{s.patient_name}</TableCell>
              <TableCell>{s.patient_phone || "—"}</TableCell>
              <TableCell>
                <span className={`text-xs font-semibold rounded-full px-2.5 py-1 ${
                  s.triage_level === "emergency" ? "bg-rose-50 text-rose-700"
                  : s.triage_level === "urgent" ? "bg-amber-50 text-amber-700"
                  : s.triage_level === "routine" ? "bg-emerald-50 text-emerald-700"
                  : "bg-slate-100 text-slate-600"}`}>{s.triage_level || "—"}</span>
              </TableCell>
              <TableCell className="max-w-[320px] truncate text-slate-600">{s.summary || "—"}</TableCell>
              <TableCell>{s.status}</TableCell>
              <TableCell>{new Date(s.created_at).toLocaleString()}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
    <TriageDrawer open={open} onOpenChange={setOpen} clinicId={clinicId} sessionId={activeId} onConvertToNote={onConvert} />
    </>
  );
}
